import React from 'react'
import { Box, Typography, Tooltip, useTheme } from '@mui/material'
import { Warning as WarningIcon, Pause as PauseIcon } from '@mui/icons-material'
import { format, parseISO, differenceInDays, isValid } from 'date-fns'
import { isOverdue as isOverdueUtil, isPaused as isPausedUtil } from '../utils/dateUtils'
import { getStatusHexColor, getLighterStatusColorForStatus } from '../utils/statusColors'
import { calculateProgressPercentage } from '../utils/progressCalculations'
import { calculateTextWidth } from '../utils/dualTrackPositioning'

interface TimelineBarProps {
  startDate: string | null
  endDate: string | null
  status: string
  summary: string
  epicRank?: number
  hasCompleteTimeline?: boolean
  // Props for proportional sizing
  minDate?: Date
  maxDate?: Date
  containerWidth?: number
  // Story points for progress display
  totalStoryPoints?: number
  completedStoryPoints?: number
}



const TimelineBar: React.FC<TimelineBarProps> = ({
  startDate,
  endDate,
  status,
  summary,
  epicRank,
  hasCompleteTimeline = false,
  minDate,
  maxDate,
  containerWidth = 450,
  totalStoryPoints = 0,
  completedStoryPoints = 0
}) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  // Using centralized utility functions
  const isOverdue = () => isOverdueUtil(endDate, status)
  const isPaused = () => isPausedUtil(status)

  // Calculate progress percentage
  const progressPercentage = calculateProgressPercentage(completedStoryPoints, totalStoryPoints, 1)

  // Calculate dynamic label width for icon positioning (narrow bars only)
  const calculateLabelWidth = (): number => {
    // TEMPORARY: Only showing progress labels, so only calculate progress width
    // If no story points, return 0 (no labels shown)
    if (totalStoryPoints === 0) {
      return 0
    }

    // Constants for label styling (matching actual CSS)
    const FONT_SIZE_REM = 0.55
    const BASE_FONT_SIZE_PX = 16
    const PADDING_HORIZONTAL = 8 // 4px each side (1px 4px in CSS)
    const BORDER_WIDTH = 2 // 1px each side

    const fontSize = FONT_SIZE_REM * BASE_FONT_SIZE_PX

    // TEMPORARY: Only calculate progress label width (duration is hidden)
    const progressText = `${progressPercentage.toFixed(1)}%`
    const progressWidth = calculateTextWidth(progressText, fontSize)
    const progressLabelWidth = progressWidth + PADDING_HORIZONTAL + BORDER_WIDTH

    return progressLabelWidth
  }

  // If no timeline data, show placeholder (theme-aware) - preserve original behavior
  if (!hasCompleteTimeline || !startDate || !endDate) {
    return (
      <Box
        sx={{
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
          borderRadius: 1,
          border: '1px dashed',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'grey.300',
          width: containerWidth,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'grey.200',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'grey.400'
          }
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {!startDate && !endDate ? 'No timeline data' :
           !startDate ? 'Missing start date' :
           'Missing end date'}
        </Typography>
      </Box>
    )
  }

  // Parse dates for validation and fallback display
  let parsedStartDate: Date | null = null
  let parsedEndDate: Date | null = null

  try {
    if (startDate && endDate) {
      parsedStartDate = parseISO(startDate)
      parsedEndDate = parseISO(endDate)

      if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) {
        throw new Error('Invalid dates')
      }
    }
  } catch (error) {
    return (
      <Box
        sx={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'error.light',
          borderRadius: 1,
          minWidth: 200
        }}
      >
        <Typography variant="body2" color="error.main">
          Invalid date format
        </Typography>
      </Box>
    )
  }

  // Calculate duration for display
  const durationDays = parsedStartDate && parsedEndDate ? differenceInDays(parsedEndDate, parsedStartDate) : 0
  const durationText = durationDays === 1 ? '1 day' : `${durationDays} days`

  // Format dates for display
  const formattedStartDate = parsedStartDate ? format(parsedStartDate, 'MMM dd, yyyy') : ''
  const formattedEndDate = parsedEndDate ? format(parsedEndDate, 'MMM dd, yyyy') : ''

  // Helper function to get status gradient (theme-aware) - preserved from original
  const getStatusGradient = (status: string) => {
    const baseColor = getStatusHexColor(status, isDarkMode)
    const opacity1 = isDarkMode ? 'dd' : 'dd'
    const opacity2 = isDarkMode ? 'aa' : 'aa'
    return `linear-gradient(135deg, ${baseColor}${opacity1}, ${baseColor}${opacity2})`
  }

  // Calculate proportional width and position using full container width (preserved from original)
  let barWidth = containerWidth * 0.95 // Default to 95% of container width
  let barLeft = containerWidth * 0.025  // Default to 2.5% offset

  if (minDate && maxDate && parsedStartDate && parsedEndDate) {
    const totalTimespan = differenceInDays(maxDate, minDate)
    const startOffset = differenceInDays(parsedStartDate, minDate)
    const endOffset = differenceInDays(parsedEndDate, minDate)

    if (totalTimespan > 0) {
      // Calculate proportional position and width using full container width
      const calculatedLeft = (startOffset / totalTimespan) * containerWidth
      const calculatedWidth = ((endOffset - startOffset) / totalTimespan) * containerWidth
      const calculatedRight = calculatedLeft + calculatedWidth

      // Improved minimum width system for better short bar appearance
      const durationDays = differenceInDays(parsedEndDate, parsedStartDate)

      // Better minimum widths for professional appearance
      let minWidth = 50 // Base minimum for all bars
      if (durationDays <= 3) {
        // Ultra-short initiatives: ensure good visibility
        minWidth = 80
      } else if (durationDays <= 7) {
        // Very short initiatives: good prominence
        minWidth = 70
      } else if (durationDays <= 14) {
        // Short initiatives: adequate width
        minWidth = 65
      }

      // Apply minimum width while preserving end position accuracy
      if (calculatedWidth < minWidth) {
        // For short bars, extend backwards to maintain accurate end position
        barWidth = minWidth
        barLeft = Math.max(0, calculatedRight - minWidth) // Extend left, keep right position
      } else {
        // For normal bars, use calculated dimensions
        barLeft = calculatedLeft
        barWidth = calculatedWidth
      }

      // Ensure bar doesn't exceed container bounds
      if (barLeft + barWidth > containerWidth) {
        barWidth = containerWidth - barLeft
      }
    }
  }

  // Calculate label width for narrow bars to position icons correctly
  const labelWidth = barWidth < 45 ? calculateLabelWidth() : 0
  const ICON_GAP = 8 // Gap between labels and icons
  const ICON_SPACING = 20 // Spacing between warning and pause icons

  return (
    <Box
      role="img"
      aria-label={`Timeline from ${formattedStartDate} to ${formattedEndDate}, duration ${durationText}, status ${status}`}
      tabIndex={0}
      sx={{
        position: 'relative',
        height: 50,
        width: containerWidth,
        '&:focus': {
          outline: 'none'
        },
        // Add pulse animation for warning icons
        '@keyframes pulse': {
          '0%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.5,
          },
          '100%': {
            opacity: 1,
          },
        },
      }}
    >
      {/* External Date Labels - Above the timeline bar */}
      {parsedStartDate && parsedEndDate && (() => {
        // For all bars: use smart spacing to prevent overlap
        const dateWidth = 40 // Approximate width of "MMM dd" text
        const minGap = 10 // Minimum gap between dates
        const requiredSpace = dateWidth * 2 + minGap // Total space needed (90px)

        const barCenter = barLeft + (barWidth / 2)
        let startPos, endPos

        if (barWidth >= requiredSpace) {
          // Bar is wide enough: position dates at bar edges with padding
          startPos = barLeft + 5
          endPos = barLeft + barWidth - dateWidth - 5
        } else {
          // Bar is too narrow: use center-based positioning with large gap
          startPos = barCenter - 55 // 55px left of center
          endPos = barCenter + 15   // 15px right of center (70px total gap)
        }

        return (
          <>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                left: startPos,
                top: -2,
                fontSize: '0.65rem',
                fontWeight: 'bold',
                color: 'text.secondary',
                whiteSpace: 'nowrap'
              }}
            >
              {format(parsedStartDate, 'MMM dd')}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                left: endPos,
                top: -2,
                fontSize: '0.65rem',
                fontWeight: 'bold',
                color: 'text.secondary',
                whiteSpace: 'nowrap'
              }}
            >
              {format(parsedEndDate, 'MMM dd')}
            </Typography>
          </>
        )
      })()}


        {/* Timeline Bar - Theme Aware with Progress Display */}
        <div
          style={{
            position: 'absolute',
            left: barLeft,
            top: '50%',
            transform: 'translateY(-50%)',
            width: barWidth,
            height: 24,
            borderRadius: '4px',
            border: `1px solid ${getStatusHexColor(status, isDarkMode)}`,
            boxShadow: isDarkMode
              ? '0 1px 3px rgba(0,0,0,0.4)'
              : '0 1px 3px rgba(0,0,0,0.1)',
            opacity: isPaused() ? 0.65 : 1,
            overflow: 'hidden',
            backgroundColor: getLighterStatusColorForStatus(status, isDarkMode, 0.25)
          }}
        >
          {/* Completed Progress Portion - Two-tone effect */}
          {totalStoryPoints > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: `${progressPercentage}%`,
                height: '100%',
                background: getStatusGradient(status),
                transition: 'width 0.3s ease-in-out'
              }}
            />
          )}

          {/* No story points - use solid color */}
          {totalStoryPoints === 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: getStatusGradient(status)
              }}
            />
          )}

          {/* Duration and Progress Percentage Labels */}
          {barWidth >= 45 && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                zIndex: 2,
                pointerEvents: 'none'
              }}
            >
              {/* TEMPORARILY HIDDEN: Duration Label - Show if no progress or if bar is wide enough */}
              {/* {(totalStoryPoints === 0 || barWidth >= 80) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.55rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {durationText}
                </Typography>
              )} */}

              {/* TEMPORARILY HIDDEN: Separator - Only show if we have both duration and progress */}
              {/* {totalStoryPoints > 0 && barWidth >= 80 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.55rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  •
                </Typography>
              )} */}

              {/* Progress Percentage - Only show if we have story points */}
              {totalStoryPoints > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.55rem',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {progressPercentage.toFixed(1)}%
                </Typography>
              )}
            </Box>
          )}
        </div>

        {/* Duration and Progress Labels Outside Bar - For Narrow Bars */}
        {barWidth < 45 && totalStoryPoints > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: barLeft + barWidth + 8,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              zIndex: 2
            }}
          >
            {/* TEMPORARILY HIDDEN: Duration */}
            {/* <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 'bold',
                fontSize: '0.55rem',
                whiteSpace: 'nowrap',
                backgroundColor: 'background.paper',
                padding: '1px 4px',
                borderRadius: '2px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              {durationText}
            </Typography> */}

            {/* Progress Percentage - Only show when story points exist */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 'bold',
                fontSize: '0.55rem',
                whiteSpace: 'nowrap',
                backgroundColor: 'background.paper',
                padding: '1px 4px',
                borderRadius: '2px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              {progressPercentage.toFixed(1)}%
            </Typography>
          </Box>
        )}

        {/* Overdue Warning Indicator - At the end of the timeline bar */}
        {isOverdue() && (
          <Tooltip title={`Overdue: End date was ${formattedEndDate}`} arrow>
            <WarningIcon
              sx={{
                position: 'absolute',
                left: barLeft + barWidth + (barWidth < 45 ? labelWidth + ICON_GAP : 5),
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 16,
                color: '#ff7043',
                animation: 'pulse 2s infinite',
                zIndex: 1,
                cursor: 'help'
              }}
            />
          </Tooltip>
        )}

        {/* Paused Status Indicator - At the right side of the timeline bar */}
        {isPaused() && (
          <Tooltip title="Timeline may be outdated - initiative paused" arrow>
            <PauseIcon
              sx={{
                position: 'absolute',
                left: barLeft + barWidth + (barWidth < 45 ? labelWidth + ICON_GAP : 5) + (isOverdue() ? ICON_SPACING : 0),
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 16,
                color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)',
                backgroundColor: isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                borderRadius: '50%',
                padding: '2px',
                zIndex: 1,
                cursor: 'help',
                boxShadow: isDarkMode
                  ? '0 1px 3px rgba(0,0,0,0.8)'
                  : '0 1px 3px rgba(0,0,0,0.3)'
              }}
            />
          </Tooltip>
        )}
    </Box>
  )
}

export default TimelineBar
